import { EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { trim } from 'lodash';
import { FONT_FAMILY_EXPORT_MAP } from '../constants';

const FONT_FAMILIES = {
  arial: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
  helvetica: 'Helvetica, "Helvetica Neue", Arial, sans-serif',
  georgia: 'Georgia, "Times New Roman", serif',
  timesNewRoman: '"Times New Roman", Times, serif',
  courierNew: '"Courier New", Courier, monospace',
  verdana: 'Verdana, Geneva, sans-serif',
  trebuchetMs: '"Trebuchet MS", Helvetica, sans-serif',
} as const;

const options = {
  defaultBlockTag: 'div',
  entityStyleFn: (entity) => {
    const entityType = entity.get('type');
    if (entityType === 'link') {
      const data = entity.getData();
      return {
        element: 'a',
        attributes: {
          href: data.src,
        },
      };
    }

    if (entityType === 'variable') {
      const data = entity.getData();

      return {
        element: 'span',
        attributes: {
          data: data.data.field,
        },
      };
    }

    if (entityType === 'IMAGE' || entityType === 'image') {
      const data = entity.getData();

      // Build style string with forced dimensions for email clients
      // Gmail and other email clients ignore width/height attributes, so we use inline CSS
      let styleString =
        'display: block; max-width: 100%; border: 0; outline: none; text-decoration: none; vertical-align: top;';

      // Force dimensions using inline CSS - email clients respect this better
      if (data.width && typeof data.width === 'number') {
        styleString += ` width: ${data.width}px !important; max-width: ${data.width}px !important;`;
      }
      if (data.height && typeof data.height === 'number') {
        styleString += ` height: ${data.height}px !important;`;
      }

      // Add alignment styles with email-friendly approach
      if (data.alignment === 'center') {
        styleString += ' margin: 0 auto !important; text-align: center;';
      } else if (data.alignment === 'right') {
        styleString += ' margin-left: auto !important; text-align: right;';
      } else {
        styleString += ' text-align: left;';
      }

      // Add email-specific compatibility styles
      styleString +=
        ' -webkit-user-drag: none; -khtml-user-drag: none; -moz-user-drag: none; -o-user-drag: none; user-drag: none;';
      styleString += ' mso-line-height-rule: exactly; -ms-interpolation-mode: bicubic;';

      const attributes: { [key: string]: string } = {
        src: data.src,
        alt: data.alt || '',
        style: styleString,
      };

      // Add width/height attributes for maximum compatibility
      if (data.width && typeof data.width === 'number') {
        attributes.width = data.width.toString();
      }
      if (data.height && typeof data.height === 'number') {
        attributes.height = data.height.toString();
      }

      return {
        element: 'img',
        attributes,
      };
    }
  },
  blockRenderers: {
    unstyled: (block) => {
      if (!trim(block.getText()).length) {
        return '<br />';
      }
    },
  },
  inlineStyles: {
    // Font family styles - match Editor component style names
    FONT_FAMILY_ARIAL: {
      element: 'span',
      attributes: { style: `font-family: ${FONT_FAMILIES.arial};` },
    },
    FONT_FAMILY_HELVETICA: {
      element: 'span',
      attributes: { style: `font-family: ${FONT_FAMILIES.helvetica};` },
    },
    FONT_FAMILY_GEORGIA: { element: 'span', attributes: { style: `font-family: ${FONT_FAMILIES.georgia};` } },
    FONT_FAMILY_TIMES_NEW_ROMAN: {
      element: 'span',
      attributes: { style: `font-family: ${FONT_FAMILIES.timesNewRoman};` },
    },
    FONT_FAMILY_COURIER_NEW: {
      element: 'span',
      attributes: { style: `font-family: ${FONT_FAMILIES.courierNew};` },
    },
    FONT_FAMILY_VERDANA: { element: 'span', attributes: { style: `font-family: ${FONT_FAMILIES.verdana};` } },
    FONT_FAMILY_TREBUCHET_MS: {
      element: 'span',
      attributes: { style: `font-family: ${FONT_FAMILIES.trebuchetMs};` },
    },

    // Font size styles - match Editor component style names
    FONT_SIZE_12: { element: 'span', attributes: { style: 'font-size: 12px;' } },
    FONT_SIZE_14: { element: 'span', attributes: { style: 'font-size: 14px;' } },
    FONT_SIZE_16: { element: 'span', attributes: { style: 'font-size: 16px;' } },
    FONT_SIZE_18: { element: 'span', attributes: { style: 'font-size: 18px;' } },
    FONT_SIZE_20: { element: 'span', attributes: { style: 'font-size: 20px;' } },
    FONT_SIZE_24: { element: 'span', attributes: { style: 'font-size: 24px;' } },
    FONT_SIZE_28: { element: 'span', attributes: { style: 'font-size: 28px;' } },
    FONT_SIZE_32: { element: 'span', attributes: { style: 'font-size: 32px;' } },

    // Text color styles - match Editor component style names
    TEXT_COLOR_000000: { element: 'span', attributes: { style: 'color: #000000;' } },
    TEXT_COLOR_333333: { element: 'span', attributes: { style: 'color: #333333;' } },
    TEXT_COLOR_666666: { element: 'span', attributes: { style: 'color: #666666;' } },
    TEXT_COLOR_999999: { element: 'span', attributes: { style: 'color: #999999;' } },
    TEXT_COLOR_CCCCCC: { element: 'span', attributes: { style: 'color: #CCCCCC;' } },
    TEXT_COLOR_FFFFFF: { element: 'span', attributes: { style: 'color: #FFFFFF;' } },
    TEXT_COLOR_FF0000: { element: 'span', attributes: { style: 'color: #FF0000;' } },
    TEXT_COLOR_FF8000: { element: 'span', attributes: { style: 'color: #FF8000;' } },
    TEXT_COLOR_FFFF00: { element: 'span', attributes: { style: 'color: #FFFF00;' } },
    TEXT_COLOR_80FF00: { element: 'span', attributes: { style: 'color: #80FF00;' } },
    TEXT_COLOR_00FF00: { element: 'span', attributes: { style: 'color: #00FF00;' } },
    TEXT_COLOR_00FF80: { element: 'span', attributes: { style: 'color: #00FF80;' } },
    TEXT_COLOR_00FFFF: { element: 'span', attributes: { style: 'color: #00FFFF;' } },
    TEXT_COLOR_0080FF: { element: 'span', attributes: { style: 'color: #0080FF;' } },
    TEXT_COLOR_0000FF: { element: 'span', attributes: { style: 'color: #0000FF;' } },
    TEXT_COLOR_8000FF: { element: 'span', attributes: { style: 'color: #8000FF;' } },
    TEXT_COLOR_FF00FF: { element: 'span', attributes: { style: 'color: #FF00FF;' } },
    TEXT_COLOR_FF0080: { element: 'span', attributes: { style: 'color: #FF0080;' } },

    // Highlight/background color styles - match Editor component style names
    HIGHLIGHT_COLOR_000000: { element: 'span', attributes: { style: 'background-color: #000000;' } },
    HIGHLIGHT_COLOR_333333: { element: 'span', attributes: { style: 'background-color: #333333;' } },
    HIGHLIGHT_COLOR_666666: { element: 'span', attributes: { style: 'background-color: #666666;' } },
    HIGHLIGHT_COLOR_999999: { element: 'span', attributes: { style: 'background-color: #999999;' } },
    HIGHLIGHT_COLOR_CCCCCC: { element: 'span', attributes: { style: 'background-color: #CCCCCC;' } },
    HIGHLIGHT_COLOR_FFFFFF: { element: 'span', attributes: { style: 'background-color: #FFFFFF;' } },
    HIGHLIGHT_COLOR_FF0000: { element: 'span', attributes: { style: 'background-color: #FF0000;' } },
    HIGHLIGHT_COLOR_FF8000: { element: 'span', attributes: { style: 'background-color: #FF8000;' } },
    HIGHLIGHT_COLOR_FFFF00: { element: 'span', attributes: { style: 'background-color: #FFFF00;' } },
    HIGHLIGHT_COLOR_80FF00: { element: 'span', attributes: { style: 'background-color: #80FF00;' } },
    HIGHLIGHT_COLOR_00FF00: { element: 'span', attributes: { style: 'background-color: #00FF00;' } },
    HIGHLIGHT_COLOR_00FF80: { element: 'span', attributes: { style: 'background-color: #00FF80;' } },
    HIGHLIGHT_COLOR_00FFFF: { element: 'span', attributes: { style: 'color: #00FFFF;' } },
    HIGHLIGHT_COLOR_0080FF: { element: 'span', attributes: { style: 'background-color: #0080FF;' } },
    HIGHLIGHT_COLOR_0000FF: { element: 'span', attributes: { style: 'background-color: #0000FF;' } },
    HIGHLIGHT_COLOR_8000FF: { element: 'span', attributes: { style: 'background-color: #8000FF;' } },
    HIGHLIGHT_COLOR_FF00FF: { element: 'span', attributes: { style: 'background-color: #FF00FF;' } },
    HIGHLIGHT_COLOR_FF0080: { element: 'span', attributes: { style: 'background-color: #FF0080;' } },
    HIGHLIGHT_NONE: { element: 'span', attributes: { style: 'background-color: transparent;' } },

    // Text alignment styles - match Editor component style names
    ALIGN_CENTER: { element: 'div', attributes: { style: 'text-align: center; display: block;' } },
    ALIGN_RIGHT: { element: 'div', attributes: { style: 'text-align: right; display: block;' } },
    ALIGN_JUSTIFY: { element: 'div', attributes: { style: 'text-align: justify; display: block;' } },

    // Additional inline styles
    STRIKETHROUGH: { element: 'span', attributes: { style: 'text-decoration: line-through;' } },
    CODE: {
      element: 'code',
      attributes: { style: 'background-color: #f1f1f1; padding: 2px 4px; border-radius: 3px; font-family: monospace;' },
    },
  },
  customStyleFn: (style) => {
    // Handle dynamic colors that might be added at runtime
    if (style.startsWith('TEXT_COLOR_')) {
      const color = style.replace('TEXT_COLOR_', '');
      return { style: `color: #${color};` };
    }

    if (style.startsWith('HIGHLIGHT_COLOR_')) {
      const color = style.replace('HIGHLIGHT_COLOR_', '');
      return { style: `background-color: #${color};` };
    }

    if (style.startsWith('FONT_FAMILY_')) {
      const fontFamily = style.replace('FONT_FAMILY_', '');
      if (FONT_FAMILY_EXPORT_MAP[fontFamily]) {
        return { style: `font-family: ${FONT_FAMILY_EXPORT_MAP[fontFamily]};` };
      }
    }

    if (style.startsWith('FONT_SIZE_')) {
      const size = style.replace('FONT_SIZE_', '');
      return { style: `font-size: ${size}px;` };
    }

    return {};
  },
};

import { imageResizeStore } from './imageResizeStore';

export function exportHTML(editorState: EditorState) {
  const contentState = editorState.getCurrentContent();
  let html = stateToHTML(contentState, options);

  const allDimensions = imageResizeStore.getAllDimensions();

  // Post-process HTML to replace image dimensions
  if (Object.keys(allDimensions).length > 0) {
    // Find and replace image tags with resized dimensions
    html = html.replace(/<img([^>]*?)src="([^"]*?)"([^>]*?)>/g, (match, _beforeSrc, src, _afterSrc) => {
      const resizedDimensions = allDimensions[src];

      if (resizedDimensions) {
        // Remove existing width/height attributes and styles
        let newMatch = match
          .replace(/\s*width="[^"]*"/g, '')
          .replace(/\s*height="[^"]*"/g, '')
          .replace(/width:\s*\d+px\s*!important;?/g, '')
          .replace(/max-width:\s*\d+px\s*!important;?/g, '')
          .replace(/height:\s*\d+px\s*!important;?/g, '');

        // Add resized dimensions
        const { width, height } = resizedDimensions;

        // Add width/height attributes
        newMatch = newMatch.replace(/(<img[^>]*?)>/, `$1 width="${width}" height="${height}">`);

        // Gmail-friendly approach: Wrap image in a sized div container
        // Gmail respects div dimensions better than image CSS

        // Clean up the image style to be minimal
        if (newMatch.includes('style="')) {
          newMatch = newMatch.replace(
            /style="([^"]*)"/,
            'style="display: block; border: 0; max-width: 100%; height: auto;"',
          );
        } else {
          newMatch = newMatch.replace(
            /(<img[^>]*?)>/,
            '$1 style="display: block; border: 0; max-width: 100%; height: auto;"',
          );
        }

        // Wrap the image in a sized div container
        const divWrapper = `<div style="width: ${width}px; height: ${height}px; overflow: hidden; display: inline-block;">`;
        const divCloser = '</div>';

        newMatch = divWrapper + newMatch + divCloser;

        return newMatch;
      }

      return match;
    });
  }

  return html;
}
